import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { useId } from '@lib/hooks';
import { cn, cva, type VariantProps } from '@lib/utils';
import { InputErrorMsg } from './InputErrorMsg';

const inputVariants = cva(
  [
    // Base
    'block w-full appearance-none bg-transparent tracking-wide',
    // Typography
    'text-fg placeholder:font-light placeholder:text-fg-muted',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-colors duration-200 ease-in-out',
    // Disabled
    'disabled:select-none',
  ],
  {
    variants: {
      variant: {
        default: [
          // Layout
          'rounded-lg border px-3 py-2 text-start',
          // Border color
          'border-line',
          // Hover
          'hover:border-line-hover',
          // Focus (uses primary context)
          'this:primary focus:border-this-solid',
          // Peer class for affix styling
          'peer',
        ],
      },
      size: {
        xs: 'h-7 text-xs px-2 py-1',
        sm: 'h-8 text-sm px-2.5 py-1.5',
        md: 'h-10 text-sm px-3 py-2',
        lg: 'h-11 text-base px-3.5 py-2.5',
        xl: 'h-12 text-base px-4 py-3',
      },
      hasError: {
        true: 'this:error border-this-solid',
        false: '',
      },
      disabled: {
        true: ['cursor-not-allowed opacity-60', 'bg-surface-raised border-line'],
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hasError: false,
      disabled: false,
    },
    compoundVariants: [
      // Override border color when hasError is true (takes precedence over default hover)
      {
        hasError: true,
        className: 'hover:border-this-solid',
      },
    ],
  }
);

const affixVariants = cva(
  ['absolute top-0 flex h-full w-9 items-center justify-center', 'transition-colors'],
  {
    variants: {
      hasError: {
        true: 'this:error text-this-text',
        false: 'text-fg-muted this:primary peer-focus:text-this-solid',
      },
    },
    defaultVariants: {
      hasError: false,
    },
  }
);

const labelVariants = cva(['text-sm font-medium text-fg']);

const descriptionVariants = cva(['mt-1 text-xs text-fg-muted']);

type InputProps = {
  label?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  description?: string;
  classNames?: {
    root?: string;
    label?: string;
    labelText?: string;
    wrapper?: string;
    input?: string;
    prefix?: string;
    suffix?: string;
    error?: string;
    description?: string;
  };
  error?: boolean | ReactNode;
  rootProps?: Record<string, unknown>;
  labelProps?: Record<string, unknown>;
} & Omit<VariantProps<typeof inputVariants>, 'hasError'> &
  Omit<ComponentPropsWithoutRef<'input'>, 'size' | 'prefix'>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      prefix,
      suffix,
      description,
      className,
      classNames = {},
      error,
      disabled = false,
      type = 'text',
      rootProps,
      labelProps,
      id,
      size = 'md',
      variant = 'default',
      ...rest
    },
    ref
  ) => {
    const inputId = useId(id, 'input');
    const hasError = !!error;

    return (
      <div className={cn('flex flex-col', classNames.root)} {...rootProps}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(labelVariants(), classNames.label)}
            {...labelProps}
          >
            <span className={classNames.labelText}>{label}</span>
          </label>
        )}

        <div className={cn('relative', label && 'mt-1.5', classNames.wrapper)}>
          <input
            className={cn(
              inputVariants({ variant, size, hasError, disabled }),
              suffix && 'pe-9',
              prefix && 'ps-9',
              className,
              classNames.input
            )}
            type={type}
            id={inputId}
            ref={ref}
            disabled={disabled}
            {...rest}
          />
          {prefix && (
            <div className={cn('start-0', affixVariants({ hasError }), classNames.prefix)}>
              {prefix}
            </div>
          )}
          {suffix && (
            <div className={cn('end-0', affixVariants({ hasError }), classNames.suffix)}>
              {suffix}
            </div>
          )}
        </div>
        <InputErrorMsg when={hasError && typeof error !== 'boolean'} className={classNames.error}>
          {error}
        </InputErrorMsg>
        {description && (
          <span className={cn(descriptionVariants(), classNames.description)}>{description}</span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants };
export type { InputProps };
